import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Language, useLanguage } from 'context/LanguageContext'
import config from './routes.json'
import { PRIVATE_TITLE, SEO_TEXTS } from './seo.texts'

/**
 * Мета-теги страницы в <head>: title, description, canonical, hreflang,
 * Open Graph, Twitter, robots.  Публичные страницы перечислены в
 * routes.json, остальные (кабинет, админка, 404) закрыты от индексации.
 *
 * Поисковики и соцсети видят эти теги без JavaScript: scripts/prerender.js
 * при сборке сохраняет каждую публичную страницу на каждом языке как
 * готовый HTML, nginx отдаёт его по адресу страницы (язык — ?lang=xx).
 */

const OG_LOCALES: Record<Language, string> = {
    en: 'en_US',
    ru: 'ru_RU',
    es: 'es_ES',
    fr: 'fr_FR',
    de: 'de_DE',
    it: 'it_IT',
    hy: 'hy_AM',
}

const normalizePath = (pathname: string) =>
    pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/'

export const pageUrl = (path: string, lang: string) =>
    config.siteUrl +
    path +
    (lang === config.defaultLanguage ? '' : `?lang=${lang}`)

const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
    if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
    }
    el.setAttribute('content', content)
}

const setCanonical = (href: string | null) => {
    let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!href) {
        el?.remove()
        return
    }
    if (!el) {
        el = document.createElement('link')
        el.rel = 'canonical'
        document.head.appendChild(el)
    }
    el.href = href
}

const setAlternates = (path: string | null) => {
    document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach(el => el.remove())
    if (!path) return
    const add = (hreflang: string, href: string) => {
        const el = document.createElement('link')
        el.rel = 'alternate'
        el.hreflang = hreflang
        el.href = href
        document.head.appendChild(el)
    }
    config.languages.forEach(lang => add(lang, pageUrl(path, lang)))
    add('x-default', pageUrl(path, config.defaultLanguage))
}

export const Seo: React.FC = () => {
    const { pathname } = useLocation()
    const { language } = useLanguage()

    useEffect(() => {
        const path = normalizePath(pathname)
        const route = config.routes.find(r => r.path === path)
        const texts = route ? SEO_TEXTS[route.key] : undefined
        const text = texts ? texts[language] ?? texts.en : undefined
        const url = route ? pageUrl(path, language) : config.siteUrl + path

        document.documentElement.lang = language
        document.title = text?.title ?? PRIVATE_TITLE

        if (text) {
            setMeta('name', 'description', text.description)
            setMeta('name', 'robots', 'index, follow')
            setMeta('property', 'og:title', text.title)
            setMeta('property', 'og:description', text.description)
            setMeta('name', 'twitter:title', text.title)
            setMeta('name', 'twitter:description', text.description)
        } else {
            // Кабинет, админка, 404 — в поиск не попадают.
            setMeta('name', 'robots', 'noindex, nofollow')
        }
        setMeta('property', 'og:url', url)
        setMeta('property', 'og:locale', OG_LOCALES[language])
        setCanonical(route ? url : null)
        setAlternates(route ? path : null)
    }, [pathname, language])

    return null
}
